import * as Modals from './Modal';
import * as Config from './config';
import * as Utils from './utils';
import * as Icons from './icons';
import { showLoader, hideLoader } from './loadIndicator';

export default class  {
    /**
     * Builds a Pharmacy
     * 
     * @constructor
     * @constructs pharmacy
     * @param {object} provider - Plain javascript object with details about a pharmacy
     * @param {Drug} drug - A Drug object
     * @param {PlacesService} placesService - A Google Maps PlacesService object 
     */
    constructor(provider, drug = '', placesService) {
        this.id = provider.ProviderIdx;
        // replace removes store number if one is present
        this.name = provider.ProviderDisplayName.toTitleCase().replace(/\d+$/, '').trim();
        this.address = provider.Address_Line1.toTitleCase();
        this.city = provider.City.toTitleCase();
        this.province = provider.ProvinceCode;
        this.postalCode = provider.PostalCode;
        this.formattedAddress = `${this.address}, ${this.city}, ${this.province}  ${this.postalCode}`;
        this.phoneNumber = Utils.formatPhoneNumber(provider.PhoneNumber);
        this.location = {
            lat: provider.Latitude,
            lng: provider.Longitude
        };
        this.drugPrice = provider.AvgUnitPriceAmt ? provider.AvgUnitPriceAmt.toFixed(2) : '';
        this.genericPrice = provider.Equivalent ? provider.Equivalent.toFixed(2) : '';
        this.dispensingFee = provider.DispensingFeeAmt ? provider.DispensingFeeAmt.toFixed(2) : '';
        this.clinicalFormulationCode = provider.ClinicalFormulationCode || '';
        this.distance = provider.distance;
        if (drug) {
            this.drug = drug.drugName;
            this.drugStrength = drug.strength;
        }
        this.preferred = provider.PPNInd.match(/[y]/i) ? 'preferred' : 'standard';
        this.specialAuthority = Boolean(provider.SACompliantInd.match(/[y]/i));
        this.placeId = provider.Geolocation;
        // Not all providers have websites, but database returns N/A for sites lacking data
        if (provider.WebSite && !provider.WebSite.match(/N\/A/i)) {
            // Covers the case where the website might be a null value
            this.website = provider.WebSite;
        } else {
            this.website = null;
        }
        this.icon = provider.icon;
        this.placesService = placesService;
        if (this.drug) {
            this.drugHtml = `
                ${Utils.drugPriceHtml(['card-info', 'card-price', 'truncate'], this.drugPrice, this.drug, this.drugStrength)}
                ${Utils.drugPriceHtml(['card-info', 'card-price', 'truncate'], this.genericPrice, Utils.i18n('genericEquivalent'), this.drugStrength)}
                <div class="card-info card-price truncate">${this.dispensingFee ? Utils.i18n('dispensingFee', this.dispensingFee) : Utils.i18n('noCurrentData', Utils.i18n('dispensingFee'))}</div>
                `;
        } else {
            this.drugHtml = ``;
        }
        this.cardHtml = `<div class="pharmacy-card" data-id="${this.id}">
                            <div class="card-column">
                                <div class="provider-icon ${this.preferred}"></div>
                                <div class="provider-icon ${this.specialAuthority ? 'special-authority' : '' }"></div>
                            </div>
                            <div class="card-column">
                                <div class="card-title truncate">${this.name}</div>
                                <div class="card-info card-distance">${(this.distance ? this.distance : 0).toFixed(1)}km </div>
                                ${this.drugHtml}
                            </div>
                        </div>
                        `;
        if (this.drug) {
            this.infoWindowDrugData = `
                ${Utils.drugPriceHtml(['iw-line', 'truncate'], this.drugPrice, this.drug, this.drugStrength)}
                ${Utils.drugPriceHtml(['iw-line', 'truncate'], this.genericPrice, Utils.i18n('genericEquivalent'), this.drugStrength)}
                <div class="iw-line truncate">${this.dispensingFee ? Utils.i18n('dispensingFee', this.dispensingFee) : Utils.i18n('noCurrentData', Utils.i18n('dispensingFee'))}</div>`;
        } else {
            this.infoWindowDrugData = ``;
        }
        this.infoWindow = new google.maps.InfoWindow({
            content: `
                <div class="iw-title">${this.name}</div>
                ${this.infoWindowDrugData}
                `
        });
    }

    /**
     * Creates and sets the marker to a map
     * 
     * @param {Map} map - A Google Maps Map object
     */
    createAndSetMarker(map) {
        if (this.preferred === 'preferred') {
            this.markerIcon = Utils.generateGoogleMapIconForSvg(Icons.getIcon(this.preferred + 'ProviderPin'), 30, 30, 0, 0, 15, 15);
            this.markerIconHover = Utils.generateGoogleMapIconForSvg(Icons.getIcon(this.preferred + 'ProviderPinHover'), 30, 30, 0, 0, 15, 15);
        } else {
            this.markerIcon = Utils.generateGoogleMapIconForSvg(Icons.getIcon(this.preferred + 'ProviderPin'), 16, 16, 0, 0, 8, 8);
            this.markerIconHover = Utils.generateGoogleMapIconForSvg(Icons.getIcon(this.preferred + 'ProviderPinHover'), 16, 16, 0, 0, 8, 8);
        }

        this.marker = new google.maps.Marker({
            position: this.location,
            title: this.name,
            icon: this.markerIcon,
            optimized: false
        });
        this.marker.addListener('click', this.showProviderDetails.bind(this));
        this.marker.setMap(map);
    }

    /**
     * Removes the marker associated with this Pharmacy
     */
    removeMarker() {
        this.marker.setMap(null);
        this.marker = null;
    }

    /**
     * Sets the highlight state icon on the map and toggles the card icon to the highlight state
     */
    highlightMarker() {
        this.marker.setIcon(this.markerIconHover);
        this.toggleIcons();
    }

    /**
     * Sets the normal state icon on the map and toggles the card icon to the normal state
     */
    normalMarker() {
        this.marker.setIcon(this.markerIcon);
        this.toggleIcons();
    }

    /**
     * Toggles the card icon between the normal and the highlight state
     */
    toggleIcons() {
        this.el.find('.provider-icon').toggleClass('white');
    }

    /**
     * Renders the pharmacy to the DOM
     * 
     * @param {selector} selector - A selector for a container div that will hold this Pharmacy's detail card
     */
    render(selector) {
        $(selector).append(this.cardHtml);
        var _this = this;
        this.el = $('.pharmacy-card[data-id="' + this.id + '"]');
        this.el.on('click', this.showProviderDetails.bind(this));
        this.el.hover(this.highlightMarker.bind(this), this.normalMarker.bind(this));
        this.marker.addListener('mouseover', () => {
            _this.el.addClass('highlight');
            _this.highlightMarker();
            _this.infoWindow.open(this.marker.get('map'), this.marker);
        });
        this.marker.addListener('mouseout', () => {
            _this.el.removeClass('highlight');
            _this.normalMarker();
            _this.infoWindow.close();
        });
    }

    /**
    * Fired onclick
    * Gets extended details and details from places in parallel
    * Then opens the modal
    * Should have a spinner or loading animation
    */
    showProviderDetails() {
        const loaderId = 'showProviderDetails';
        let hasDrug = Boolean(this.drug);
        showLoader(loaderId);
        var _this = this;
        this.getDetailsFromPlaces()
            .then((results) => {
                _this.placesData = results;
                let detailModal = new Modals.DetailModal({ drugData: hasDrug });
                detailModal.openModal(_this, '#detailsModal');
                hideLoader(loaderId);
            })
        .catch(error => {
            _this.placesData = {};
            let detailModal = new Modals.DetailModal({ drugData: hasDrug });
            detailModal.openModal(_this, '#detailsModal');
            hideLoader(loaderId);
        });
    }

    /**
     * If the database doesn't have a google place id, this function attempts to find one
     * @returns {promise} Promise collecting the results of the places service nearby search
     */
    getDetailsFromPlaces() {
        // mislabelled in the database, produces bad results against the places api
        let query = this.name.match(/pricesmart/i) ? "save on foods" : this.name;
        var _this = this;

        let request = {
            location: this.location,
            radius: Config.PLACES_SEARCH_RADIUS,
            keyword: query,
            type: 'establishment'
        };

        return new Promise((resolve, reject) => {
            this.placesService.nearbySearch(request, (results, status) => {
                if (status !== google.maps.places.PlacesServiceStatus.OK) {
                    reject(status);
                }
                else {
                    _this.getExtendedDetailsFromPlaces(results[0].place_id)
                        .then(results => {
                            resolve(results);
                        })
                        .catch(err => {
                            reject(err);
                        });
                }
            });
        });
    }

    /**
    * Gets extended details from the places service
    *
    * @param {string} A Google Maps Place Id
    * @returns {promise} a promise containing the results of the getDetails request
    */
    getExtendedDetailsFromPlaces(placeId) {
        let request = {
            placeId: placeId
        };

        return new Promise((resolve, reject) => {
            this.placesService.getDetails(request, (results, status) => {
                if (status !== google.maps.places.PlacesServiceStatus.OK) {
                    reject(status);
                }
                else {
                    resolve(results);
                }
            })
        })
    }
}


// WEBPACK FOOTER //
// ../D:/builds/ACES/Pharmacy Compass Production Support/PharmacyCompass2/Content/scripts/pharmacy.js